/*
 * Copyright 2023 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { GeneratorError } from "./GeneratorError.js";
import { wirePropertyV2ToSdkPrimaryKeyTypeDefinition } from "./wirePropertyV2ToSdkPrimaryKeyTypeDefinition.js";
import { wirePropertyV2ToSdkPropertyDefinition } from "./wirePropertyV2ToSdkPropertyDefinition.js";
export function wireObjectTypeFullMetadataToSdkObjectMetadata(objectTypeWithLink, v2, log) {
  if (objectTypeWithLink.objectType.properties[objectTypeWithLink.objectType.primaryKey] === undefined) {
    throw new GeneratorError("Primary key not found in object type", {
      primaryKey: objectTypeWithLink.objectType.primaryKey,
      objectTypeApiName: objectTypeWithLink.objectType.apiName
    });
  }

  // saved ontology.json files may not have this implementsInterfaces2 so we need to handle
  if (objectTypeWithLink.implementsInterfaces2 == null && objectTypeWithLink.implementsInterfaces != null) {
    throw new Error("Your ontology.json file is missing the implementsInterfaces2 field. Please regenerate it.");
  }
  const interfaceImplementations = {};
  const interfaceMap = objectTypeWithLink.implementsInterfaces2 ? Object.fromEntries(Object.entries(objectTypeWithLink.implementsInterfaces2).sort(([a], [b]) => a.localeCompare(b)).map(([interfaceApiName, impl]) => {
    // prefer V2 if available and non-empty
    if (impl.propertiesV2 && Object.keys(impl.propertiesV2).length > 0) {
      const propMap = {};
      const implMap = {};
      for (const [iptApiName, implementation] of Object.entries(impl.propertiesV2)) {
        const converted = convertInterfacePropertyImplementation(implementation);
        if (converted == null) continue;
        implMap[iptApiName] = converted;
        if (converted.type === "localProperty") {
          propMap[iptApiName] = converted.propertyApiName;
        }
      }
      if (Object.keys(implMap).length > 0) {
        interfaceImplementations[interfaceApiName] = implMap;
      }
      return [interfaceApiName, propMap];
    }
    // fall back to V1
    return [interfaceApiName, impl.properties];
  })) : {};
  return {
    type: "object",
    apiName: objectTypeWithLink.objectType.apiName,
    description: objectTypeWithLink.objectType.description,
    primaryKeyApiName: objectTypeWithLink.objectType.primaryKey,
    primaryKeyType: wirePropertyV2ToSdkPrimaryKeyTypeDefinition(objectTypeWithLink.objectType.properties[objectTypeWithLink.objectType.primaryKey]),
    links: Object.fromEntries([...objectTypeWithLink.linkTypes].sort((a, b) => a.apiName.localeCompare(b.apiName)).map(linkType => {
      return [linkType.apiName, {
        multiplicity: linkType.cardinality === "MANY",
        targetType: linkType.objectTypeApiName
      }];
    })),
    properties: Object.fromEntries(Object.entries(objectTypeWithLink.objectType.properties).map(([key, value]) => [key, wirePropertyV2ToSdkPropertyDefinition(value, !(v2 && objectTypeWithLink.objectType.primaryKey === key), log)]).filter(([_, value]) => value != null).sort(([a], [b]) => a.localeCompare(b))),
    implements: objectTypeWithLink.implementsInterfaces ? [...objectTypeWithLink.implementsInterfaces].sort((a, b) => a.localeCompare(b)) : objectTypeWithLink.implementsInterfaces,
    interfaceMap,
    inverseInterfaceMap: Object.fromEntries(Object.entries(interfaceMap).map(([interfaceApiName, props]) => [interfaceApiName, invertProps(props)])),
    ...(Object.keys(interfaceImplementations).length > 0 ? {
      interfaceImplementations
    } : {}),
    icon: supportedIconTypes.includes(objectTypeWithLink.objectType.icon.type) ? objectTypeWithLink.objectType.icon : undefined,
    titleProperty: objectTypeWithLink.objectType.titleProperty,
    displayName: objectTypeWithLink.objectType.displayName,
    pluralDisplayName: objectTypeWithLink.objectType.pluralDisplayName,
    status: ensureStringEnumSupportedOrUndefined(objectTypeWithLink.objectType.status, supportedReleaseStatus),
    rid: objectTypeWithLink.objectType.rid,
    visibility: ensureStringEnumSupportedOrUndefined(objectTypeWithLink.objectType.visibility, supportedObjectTypeVisibility)
  };
}
function invertProps(a) {
  return a ? Object.fromEntries(Object.entries(a).map(([k, v]) => [v, k])) : undefined;
}
function convertInterfacePropertyImplementation(wire) {
  switch (wire.type) {
    case "localPropertyImplementation":
      return {
        type: "localProperty",
        propertyApiName: wire.propertyApiName
      };
    case "structFieldImplementation":
      return {
        type: "structField",
        propertyApiName: wire.structFieldOfProperty.propertyApiName,
        structFieldApiName: wire.structFieldOfProperty.structFieldApiName
      };
    case "structImplementation":
      return {
        type: "struct",
        mapping: Object.fromEntries(Object.entries(wire.mapping).map(([fieldName, entry]) => {
          if (entry.type === "structFieldOfProperty") {
            return [fieldName, {
              type: "structFieldOfProperty",
              propertyApiName: entry.propertyApiName,
              structFieldApiName: entry.structFieldApiName
            }];
          }
          return [fieldName, {
            type: "property",
            propertyApiName: entry.propertyApiName
          }];
        }))
      };
    case "reducedPropertyImplementation":
      {
        const inner = convertNestedInterfacePropertyImplementation(wire.implementation);
        if (inner == null) {
          return undefined;
        }
        return {
          type: "reduced",
          implementation: inner
        };
      }
    default:
      return undefined;
  }
}
function convertNestedInterfacePropertyImplementation(wire) {
  const converted = convertInterfacePropertyImplementation(wire);
  if (converted == null || converted.type === "reduced") return undefined;
  return converted;
}
export const supportedIconTypes = ["blueprint"];
export const supportedReleaseStatus = ["ACTIVE", "EXPERIMENTAL", "DEPRECATED", "ENDORSED"];
export const supportedObjectTypeVisibility = ["NORMAL", "PROMINENT", "HIDDEN"];
export function ensureStringEnumSupportedOrUndefined(value, supportedValues) {
  return value && supportedValues.includes(value) ? value : undefined;
}